/**
 * ErrorBoundary — catches unhandled React render errors and shows a
 * graceful recovery screen instead of a blank white page.
 *
 * Usage (in App.jsx):
 *   import ErrorBoundary from './components/ErrorBoundary.jsx'
 *   <ErrorBoundary><App /></ErrorBoundary>
 */

import { Component } from 'react'
import { reportBoundaryError } from '../utils/errorLogger.js'
import { captureException as sentryCapture } from '../utils/sentry.js'

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info)
    // Forward to errorReports so admins see the render crash alongside
    // uncaught globals. Fire-and-forget — the boundary must never throw.
    try { reportBoundaryError(error, info) } catch {}
    // Also forward to Sentry (no-op if DSN unset). componentStack helps
    // Sentry symbolicate the React tree.
    try { sentryCapture(error, { contexts: { react: { componentStack: info?.componentStack } } }) } catch { /* logging the logger is a tarpit */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div style={{
        minHeight: '100vh',
        background: '#0E0C12',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
        textAlign: 'center',
        gap: 24,
      }}>
        {/* Logo — uses dark variant always; ErrorBoundary bg is dark in
            both themes and this component renders outside ThemeProvider
            so the theme-aware <Logo /> hook isn't available here. */}
        <img loading="lazy" src="/vinarai_Logo_dark.svg"
          alt="Vinarai"
          style={{ width: 160, height: 'auto', opacity: 0.7 }} />

        {/* Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(58,13,24,0.5)',
          border: '1px solid rgba(196,150,90,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="rgba(196,150,90,0.7)" strokeWidth="1.5"/>
            <path d="M12 7v5" stroke="rgba(196,150,90,0.9)" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="16.5" r="1.2" fill="rgba(196,150,90,0.9)"/>
          </svg>
        </div>

        <div style={{ maxWidth: 320 }}>
          <h1 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 26, fontWeight: 400,
            color: '#F4EFE7', margin: '0 0 10px',
            letterSpacing: '0.02em',
          }}>
            Something went wrong
          </h1>
          <p style={{
            fontFamily: 'Raleway, sans-serif',
            fontSize: 13, color: 'var(--app-text-tertiary)',
            lineHeight: 1.6, margin: 0,
          }}>
            An unexpected error occurred. Please refresh the page to continue.
          </p>
        </div>

        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '13px 36px',
            borderRadius: 50,
            background: 'linear-gradient(135deg, #C4965A, #a07840)',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Raleway, sans-serif',
            fontSize: 13, fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#0E0C12',
          }}
        >
          Refresh Page
        </button>

        {process.env.NODE_ENV === 'development' && this.state.error && (
          <pre style={{
            fontFamily: 'monospace', fontSize: 11,
            color: 'var(--app-text-disabled)',
            maxWidth: 480, overflow: 'auto',
            textAlign: 'left', padding: 16,
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 8, marginTop: 8,
          }}>
            {String(this.state.error)}
          </pre>
        )}
      </div>
    )
  }
}
